import React from 'react';
import PropTypes from 'prop-types';

import { Logger } from 'aws-amplify';

import { 
  AppBar,
  Button,
  Chip,
  CircularProgress,
  InputBase,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core';

import { fade, makeStyles, useTheme } from '@material-ui/core/styles';

import {
  BugReport as ErrorsIcon,  
  Search as SearchIcon,
  Warning as LogTruncatedIcon,
  CheckCircleOutline as LogLoadedIcon,
  NavigateBefore as PreviousMatchIcon,
  NavigateNext as NextMatchIcon,
} from '@material-ui/icons';

import { GroupedVirtuoso } from 'react-virtuoso';

import { useExecutionInputLogCloudWatch } from "..";

import { Message } from '../../utils/components';

import { ExecutionInputLogEvent } from ".";

const logger = new Logger('ExecutionInputLog');

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  group: {
    backgroundColor: theme.palette.background.default,
    padding: '4px 8px',
    border: '2px'
  },
  toolbar: {
    backgroundColor: theme.palette.grey.A700,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  metrics: {
    borderRadius: '4px',
    paddingLeft: '8px',
    marginRight: '8px',
    boxShadow: '1px 1px 2px #555 inset',
  },
  metricsLabel: {
    paddingLeft: '4px'
  },
  errors: {
    borderRadius: '4px',
    paddingLeft: '8px',
    marginRight: '8px',
  },
  errorsLabel: {
    paddingLeft: '4px'
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: fade(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(3),
      width: 'auto',
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
    },
    alignItems: 'center',
    justifyContent: 'center',
  },
  navIcons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
    },
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
  matchNav: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(3),
    },
  },
  matchNavPreviousIcon: {
    color: 'rgba(0, 0, 0, 0.26)',
    "&:hover": {
      color: 'rgba(0, 0, 0, 0.4)',
    },
  },
  grow: {
    flexGrow: 1,
  },
  attempts: {
    display: 'flex',
    alignItems: 'center',
  },
  attemptLabel: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
    paddingLeft: '16px',
  },
}));

function AttemptGroups(props) {
  const { groups, onSelected } = props;

  const classes = useStyles();
  
  const handleClick = (groupIndex) => {
    if(onSelected) {
      onSelected(groupIndex);
    }    
  };
  
  if(groups.length>1) {
    return(
      <div className={classes.attempts}>
        <Typography className={classes.attemptLabel} variant="overline">Attempt:</Typography>
        {groups.map((group, index) => 
          <Tooltip 
            key={"i"+index}
            arrow placement="top-end"
            title={`Jump to log events for attempt #${index+1}`} >
            <Button 
              variant="contained" 
              size="small"
              //color="inherit" 
              onClick={() => handleClick(index)}
              style={{minWidth: '32px', marginLeft: '4px'}}
            >
              {index+1}
            </Button>
          </Tooltip>
        )}
      </div>
    );
  } else {
    return(null);
  }
}

function LogStatusIcon(props) {
  const { loading, truncated } = props;
  const theme=useTheme();
  if(loading) { 
    return(<CircularProgress style={{ margin: '2px' }} size={16} thickness={5}/>);
  } else if(truncated) {
    return(
        <LogTruncatedIcon fontSize="small" 
            style={{ 
              color: theme.palette.warning.main,
            }}
        />
    );
  } else {
    return(<LogLoadedIcon style={{ color: theme.palette.success.main }} fontSize="small"/>);
  }
}

const errorRegex=/\b(?:error|failure|failed)\b/gi;


function getEventIndices(logEvents, matchPattern, initialGroup, initialMatch ) {

  let groupEventCount=0;
  let initialMatchIndex = -1;    // if initial match was passed from parent and found, this is the index to pass to virtuoso
  
  const groups = [];             // group label for each attempt in log
  const groupCounts = [];        // number of logEvent items in each attempt
  const groupStartIndices = [];  // tracks logEvent index of attempt start
  
  const matchEventIndices = [];  // tracks logEvent index of each line with a match to search pattern
  const errorEventIndices = [];  // tracks logEvent index of each line with a match to ERROR search pattern

  const matchRegex=matchPattern.length>0?new RegExp(matchPattern, "i"):null;

  if(logEvents) {

    logEvents.forEach((event, index) => {
      if(event.message.startsWith('INPUT: size=')) {
        if(groupEventCount>0) {
          groupCounts.push(groupEventCount);
        }
        groupEventCount=0;
        groupStartIndices.push(index);
      } else if(event.message.startsWith('START: memory=')) {
        const dttm=new Date(event.timestamp);
        groups.push(`Attempt #${groups.length+1}: ${dttm.toLocaleDateString()} ${dttm.toLocaleTimeString()}`);
      }
      groupEventCount++;
      if(event.message.match(errorRegex)) {
        errorEventIndices.push(index);
      }
      if(matchRegex) {
        if(event.message.match(matchRegex)) {
          matchEventIndices.push(index);
        }
      }
      if(initialMatchIndex<0 && initialMatch && initialMatch===event.message) {
        if(initialGroup) {
          if(initialGroup===(groups.length-1)) {
            initialMatchIndex=index;
          }
        } else {
          initialMatchIndex=index;
        }
      }
      
    });
    if(groupEventCount>0) {
      groupCounts.push(groupEventCount);
    }
  }
  
  return {
    groups,
    groupCounts,
    groupStartIndices,
    matchEventIndices,
    errorEventIndices,
    initialMatchIndex,
  };
}

export default function ExecutionInputLog(props) {
  const { executionId, inputUri, initialGroup, initialMatch } = props;
  const theme = useTheme();
  const classes = useStyles();
  const virtuoso = React.useRef(null);
  const { logEvents, loading, error, truncated } = useExecutionInputLogCloudWatch({ executionId, inputUri });

  const [ matchPattern, setMatchPattern ] = React.useState("");
  const [ matchIndex, setMatchIndex ] = React.useState(-1);
  const [ errorIndex, setErrorIndex ] = React.useState(-1);

  const [ selectedIndex, setSelectedIndex ] = React.useState(-1);

  const isSmall = useMediaQuery(theme.breakpoints.down('xs'));

  const { 
    groups,
    groupCounts,
    groupStartIndices,
    matchEventIndices,
    errorEventIndices,
  } = getEventIndices(logEvents, matchPattern, initialGroup, initialMatch );
  
  const handleScrollToGroup = (index) => {
    if(index<groupStartIndices.length) {
      handleScrollToIndex(groupStartIndices[index]);
    }
  };
  const handleScrollToMatchIndex = (newMatchIndex, newEventIndex=null) => {
    setMatchIndex(newMatchIndex);
    handleScrollToIndex(matchEventIndices[newMatchIndex],true);
  };
  const handleScrollToIndex = (index, useMatchEffect=false) => {
    let align='start';
    let behavior='auto';
    if(useMatchEffect) {
      setSelectedIndex(index);
      align='center';
      behavior='smooth';
    }
    if(virtuoso.current) {
      virtuoso.current.scrollToIndex({
        index,
        align,
        behavior,
      });
    }
  };
  const handleMatchPattern = (event) => {
    setMatchPattern(event.target.value);
    setMatchIndex(-1);
  };
  const handleShowNextMatch = () => {
    handleScrollToMatchIndex(matchIndex<(matchEventIndices.length-1)?matchIndex+1:0);
  };
  const handleShowPreviousMatch = (prevState) => {
    handleScrollToMatchIndex(matchIndex>0?matchIndex-1:matchEventIndices.length-1);
  };
  const handleMatchErrors = () => {
    const newErrorIndex=errorIndex<(errorEventIndices.length-1)?errorIndex+1:0;
    setErrorIndex(newErrorIndex);
    handleScrollToIndex(errorEventIndices[newErrorIndex],true);
  };

  React.useEffect(() => {
    if(logEvents && logEvents.length>0 && (initialGroup || initialMatch)) {
      const { groupStartIndices, initialMatchIndex } = getEventIndices(logEvents, "", initialGroup, initialMatch );
      if(initialMatchIndex>=0) {
        handleScrollToIndex(initialMatchIndex,true);
      } else if(initialGroup>=0) {
        if(initialGroup<groupStartIndices.length) {
          handleScrollToIndex(groupStartIndices[initialGroup]);
        }
      }
    }
  }, [logEvents, initialGroup, initialMatch]);

  if(error) {
    logger.error(error);
    return(
      <Message 
        severity="error" 
        duration={30000}
        value="An error has occurred loading the execution input log from the server."
      />
    );
  }
  
  if(!Array.isArray(logEvents) || !logEvents.length) {
    return(null);
  }

  let logStatusMessage="Loading log entries...";
  if(!loading) {
    if(truncated) {
      logStatusMessage=`Reached log size limit! Viewing first ${logEvents.length} log event${logEvents.length===1?'':'s'}`;
    } else {
      logStatusMessage=`Viewing all ${logEvents.length} log event${logEvents.length===1?'':'s'}`;
    }
  }
  
  return (
    <div className={classes.root}>
      <AppBar color="primary" position="static">
        <Toolbar variant="dense" className={classes.toolbar}>
          <Tooltip 
            arrow placement="top-end"
            title={logStatusMessage}>
            <Chip 
              classes={{ root: classes.metrics, label: classes.metricsLabel }}
              color="default"
              icon={<LogStatusIcon loading={loading} truncated={truncated}/>} 
              label={logEvents.length}/>
          </Tooltip>
          {errorEventIndices.length>0?
            <Tooltip 
              arrow placement="top-end"
              title={`${errorEventIndices.length} line${errorEventIndices.length===1?'':'s'} with errors`}>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => handleMatchErrors()}
                style={{minWidth: '32px', marginLeft: '4px'}}
              >
                <ErrorsIcon style={{ color: theme.palette.error.dark }}/>{isSmall?null:errorEventIndices.length}
              </Button>
            </Tooltip>
            :null
          }
          <div className={classes.grow}/>
          <Tooltip 
            arrow placement="top-end"
            title="Search log events for matching patterns" >
            <div className={classes.search}>
              <div className={classes.searchIcon}>
                <SearchIcon />
              </div>
              <InputBase
                value={matchPattern}
                placeholder="Search…"
                classes={{
                  root: classes.inputRoot,
                  input: classes.inputInput,
                }}
                inputProps={{ 'aria-label': 'search' }}
                onChange={handleMatchPattern}
              />
            </div>
          </Tooltip>
          <Chip 
            classes={{ root: classes.matchNav, icon: classes.matchNavPreviousIcon }}
            clickable
            disabled={!matchEventIndices.length}
            icon={<PreviousMatchIcon onClick={handleShowPreviousMatch}/>} 
            label={matchIndex>=0?`${matchIndex+1} / ${matchEventIndices.length}`:matchEventIndices.length?matchEventIndices.length:""}
            deleteIcon={<NextMatchIcon/>}
            onDelete={handleShowNextMatch}
          />
          <div className={classes.grow}/>
          <AttemptGroups groups={groups} onSelected={handleScrollToGroup}/>
        </Toolbar>
      </AppBar>
      <GroupedVirtuoso 
        ref={virtuoso}
        // initialTopMostItemIndex={initialMatchIndex<0?0:initialMatchIndex}
        style={{ height: '400px', }} 
        itemContent={index => {
          if(logEvents[index]) {
            return(
              <div style={{ backgroundColor: 'white' }}>
                <ExecutionInputLogEvent 
                  index={index} 
                  selected={index===selectedIndex}
                  timestamp={logEvents[index].timestamp} 
                  message={logEvents[index].message}
                  timeFormat='time'
                />
              </div>
            )}
          else {
            return(<div>foo</div>);
          }
        }}
        groupCounts={groupCounts}
        groupContent={index => {
          return(
            <div className={classes.group}>
              <Typography variant="button">{groups[index]}</Typography>
            </div>
          );
        }}
        components={{
          Footer: () => {
            return(
              <Typography component="div" variant="caption">{truncated?`Log viewer limit reached at line ${logEvents.length}... additional events not shown!`:"End of log"}</Typography>
            );
          },
        }}
      />
    </div>
  );
}

ExecutionInputLog.propTypes = {
  executionId: PropTypes.string.isRequired,
  inputUri: PropTypes.string.isRequired,
  initialGroup: PropTypes.number,
  initialMatch: PropTypes.string,
};

ExecutionInputLog.defaultProps = {
};